import { Component, Input, OnDestroy, OnInit, OnChanges } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { TableDatasource, DataTypes, TableColumn } from '@gamelearn/skeletor/models';
import { TranslateService } from '@ngx-translate/core';
import { MarsGroupService } from '../../services/mars-groups.service';
import { Router } from '@angular/router';
import { MarsGroupList } from '../../models/MarsGroup';

@Component({
  selector: 'lms-mars-group-list',
  templateUrl: './mars-group-list.component.html',
  styleUrls: ['./mars-group-list.component.scss']
})
export class MarsGroupListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public listId: string;
  @Input() public clientId: string;
  @Input() public groupList: MarsGroupList;
  public dataSource: any;
  private userRole: any;
  constructor(
    private profileService: ProfileService,
    private translate: TranslateService,
    private listHelperService: ListHelperService,
    private marsService: MarsGroupService,
    public router: Router
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }
  public ngOnInit(): void {}

  async ngOnChanges() {
    this.dataSource = await this.getDataSource(this.listId, this.groupList);
  }

  ngOnDestroy() {
    StoreService.remove(this.listId);
  }

  onClickRow(group) {
    this.router.navigate(['/mars', group.id]);
  }

  onDatasourceUpdate(dataSource) {
    this.dataSource = dataSource;
    StoreService.update(this.listId, this.dataSource);
  }

  async getDataSource(listId, options: any = {}) {
    const dataSource = new TableDatasource({
      sort: '-startDate',
      columns: await this.getColumns(),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { clientId, agentIds, distributorId, state, ...$filterValues } = filters;
    const $filter = this.listHelperService.buildFilterString($filterValues, dataSource);
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      clientId,
      agentIds,
      distributorId,
      state,
      $filter
    };
    return this.marsService.fetch(paginationOptions).then((response) => response);
  }

  /**
   * Set the data of the table
   * @param dataSource
   */
  public setDatasourceUpdate(dataSource: any): void {
    this.dataSource = dataSource;
    StoreService.update(this.listId, this.dataSource);
  }

  /**
   * Get the columns of the table
   * @private
   */
  async getColumns() {
    const columns = [
      {
        source: 'name',
        label: this.translate.instant('nouns.name'),
        sortable: true,
        style: { width: '160px' },
        cellRenderer: (renderer, value, group) =>
          renderer`<div class="ellipsis">${this.listHelperService.getRouterLinkRenderer(
            renderer,
            `/mars/${group.id}`,
            value
          )}</div>`
      },
      // {
      //   source: 'distributorId',
      //   label: this.translate.instant('nouns.distributor'),
      //   visible: false,
      //   style: { width: '160px' },
      //   showInColumnChooser: this.profileService.canExecute('groups.tableColumns.partner'),
      //   valueParser: (value, group) => (value ? group.partner.name : ''),
      //   cellRenderer: (renderer, value, group) =>
      //     renderer`<div class="ellipsis">${this.listHelperService.getRouterLinkRenderer(
      //       renderer,
      //       `/distributors/${group.distributorId}`,
      //       value
      //     )}</div>`,
      //   searchRenderer: (renderer, value, callback) => {
      //     return renderer`
      //       <gl-input-tag
      //         .optionLabel="${this.translate.instant('actions.select')}"
      //         .options="${this.partnerQuestion.options}"
      //         .createTags="${false}"
      //         .value="${value}"
      //         tagsLimit="1"
      //         small
      //         @onKeyup="${(ev) => this.onSearchPartners(ev)}"
      //         @onChange="${(ev) => callback(ev.detail.value)}"
      //       >
      //       </gl-input-tag>
      //     `;
      //   }
      // },
      {
        source: 'clientName',
        label: this.translate.instant('groups.header.clientName'),
        sortable: true,
        style: { width: '160px' },
        visible: this.profileService.canExecute('groups.tableColumns.clientName'),
        showInColumnChooser: this.profileService.canExecute('groups.tableColumns.clientName'),
        cellRenderer: (renderer, value, group) =>
          renderer`<div class="ellipsis">${this.listHelperService.getRouterLinkRenderer(
            renderer,
            `/clients/${group.clientId}`,
            value
          )}</div>`
      },
      {
        source: 'startDate',
        label: this.translate.instant('nouns.startDate'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '110px' },
        valueParser: (value, group) => this.listHelperService.parseDate(value, group.timezone)
      },
      {
        source: 'endDate',
        label: this.translate.instant('nouns.endDate'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '110px' },
        valueParser: (value, group) => this.listHelperService.parseDate(value, group.timezone)
      },
      // {
      //   source: 'state',
      //   label: this.translate.instant('nouns.status'),
      //   style: { width: '130px' },
      //   valueParser: (value) => this.translate.instant(`properties.${value}`),
      //   searchRenderer: (renderer, value, callback) => {
      //     const options = [
      //       { key: 'any', value: this.translate.instant('properties.any') },
      //       { key: 'active', value: this.translate.instant('properties.active') },
      //       { key: 'finished', value: this.translate.instant('properties.finished') },
      //       { key: 'future', value: this.translate.instant('properties.future') }
      //     ];
      //     return renderer`<gl-select small .options="${options}" .value="${value || 'any'}" @onChange="${(e) =>
      //       callback(e.detail.value !== 'any' ? e.detail.value : '')}" ></gl-select>`;
      //   }
      // },
      // {
      //   source: 'agentIds',
      //   label: this.translate.instant('agents.label'),
      //   sortable: false,
      //   style: { width: '200px' },
      //   visible: false,
      //   showInColumnChooser: this.profileService.canExecute('groups.tableColumns.agents'),
      //   valueParser: (value, group) => {
      //     const { agents = [] } = group;
      //     if (agents && agents.length) {
      //       return agents.map((agent) => this.listHelperService.parseAgent(agent));
      //     }
      //     return '';
      //   },
      //   cellRenderer: (renderer, value) => {
      //     const colorTrigger = ['#e1e4e8'];
      //     const colorDefault = ['#c3ccdc'];
      //     if (value && value.length) {
      //       const [firstAgent, ...restAgents] = value;
      //       return renderer`
      //         <div class="tags">
      //           ${renderer`<gl-tag .colors="${colorDefault}" label="${firstAgent}"></gl-tag>`}
      //           ${
      //             restAgents.length === 0
      //               ? ''
      //               : renderer`
      //                 <gl-tooltip posLeft light>
      //                   <gl-tag slot="trigger" .colors="${colorTrigger}" label="+${restAgents.length}"></gl-tag>
      //                   ${restAgents.map((agent) => renderer`<gl-tag label="${agent}"></gl-tag>`)}
      //                 </gl-tooltip>
      //               `
      //           }
      //           </div>
      //           `;
      //     }
      //     return '';
      //   },
      //   searchRenderer: (renderer, value, callback) => {
      //     return renderer`
      //       <gl-input-tag
      //         .value="${value}"
      //         .options="${this.agentQuestion.options}"
      //         tagsLimit="1"
      //         small
      //         .createTags="${false}"
      //         .optionLabel="${this.translate.instant('actions.select')}"
      //         @onKeyup="${(ev) => this.onSearchAgents(ev)}"
      //         @onChange="${(ev) => callback(ev.detail.value)}"
      //       >
      //       </gl-input-tag>
      //     `;
      //   }
      // },
      {
        source: 'tags',
        label: this.translate.instant('nouns.tags'),
        visible: this.userRole.name === 'clientAdmin',
        style: { width: '200px' },
        dataType: DataTypes.array
      }
    ];
    return columns;
  }
}
